<template>
  <main
    class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased"
    :style="{
      '--pointer-x': `${pointer.x}%`,
      '--pointer-y': `${pointer.y}%`
    }"
    @pointermove="handlePointerMove"
  >
    <!-- AMBIENTE -->
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
      <div class="ambient-orb ambient-orb-three"></div>
    </div>

    <!-- VOLTAR AO TOPO -->
    <transition name="scroll-top">
      <button
        v-show="showScrollTop"
        type="button"
        class="fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo"
        title="Voltar ao topo"
        @click="scrollToTop"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          class="h-5 w-5"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <!-- HERO -->
    <section class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <div class="mx-auto max-w-5xl text-center" data-reveal>
          <div
            class="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--glass)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-2xl"
          >
            <span class="relative flex h-2 w-2">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-40"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>

            Grivaro Journal

            <span class="hidden text-[var(--muted-2)] sm:inline">
              / Technology radar
            </span>
          </div>

          <h1
            class="mx-auto mt-7 max-w-5xl text-5xl font-semibold tracking-[-0.065em] text-[var(--text-strong)] sm:text-6xl lg:text-[5.35rem] lg:leading-[0.98]"
          >
            Tecnologia em movimento.
            <span class="hero-gradient block">Contexto para decidir melhor.</span>
          </h1>

          <p
            class="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[var(--muted)] sm:text-xl"
          >
            Artigos, referências e notícias sobre desenvolvimento, arquitetura,
            IA, produto, design, frontend, backend e operação digital.
          </p>

          <div class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              @click="refresh"
              class="primary-button group inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold"
            >
              Atualizar feed

              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                class="h-4 w-4 transition duration-500 group-hover:rotate-180"
                aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M20 11a8.1 8.1 0 0 0-15.5-2M4 4v5h5M4 13a8.1 8.1 0 0 0 15.5 2M20 20v-5h-5" />
              </svg>
            </button>

            <router-link
              to="/solutions"
              class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold"
            >
              Ver soluções
            </router-link>
          </div>
        </div>

        <!-- TECH RADAR -->
        <div class="relative mx-auto mt-16 max-w-6xl" data-reveal>
          <div class="hero-halo absolute inset-x-16 top-12 h-44 rounded-full blur-3xl"></div>

          <div class="radar-frame relative rounded-[2.4rem] p-2.5 sm:p-3">
            <div class="overflow-hidden rounded-[1.9rem] border border-white/10 bg-[#08090b] text-white sm:rounded-[2.25rem]">
              <div
                class="flex items-center justify-between border-b border-white/[0.07] px-5 py-4 sm:px-6"
              >
                <div class="flex gap-2">
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                </div>

                <div
                  class="hidden rounded-full bg-white/[0.06] px-4 py-2 text-[10px] font-semibold text-zinc-400 ring-1 ring-white/10 sm:block"
                >
                  Grivaro / tech-radar
                </div>

                <span
                  class="inline-flex items-center gap-2 rounded-full bg-white/[0.07] px-3 py-1.5 text-[10px] font-semibold text-zinc-300 ring-1 ring-white/10"
                >
                  <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  Live
                </span>
              </div>

              <div class="grid gap-8 p-5 sm:p-7 lg:grid-cols-[1fr_.85fr] lg:p-9">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">
                    Tech Radar
                  </p>

                  <h2
                    class="mt-4 max-w-2xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl"
                  >
                    Um feed vivo para acompanhar o que realmente importa.
                  </h2>

                  <p class="mt-5 max-w-xl text-sm leading-7 text-zinc-400">
                    Conteúdo próprio e fontes externas organizados em uma experiência
                    única, com fallback local para o feed nunca depender de uma API só.
                  </p>

                  <div class="mt-8 grid gap-3 sm:grid-cols-3">
                    <article
                      v-for="item in highlights"
                      :key="item.title"
                      class="radar-card rounded-[1.35rem] p-4"
                    >
                      <div class="mb-3 h-1 w-8 rounded-full bg-white/25"></div>
                      <p class="text-xs font-semibold text-white">
                        {{ item.title }}
                      </p>
                      <p class="mt-1.5 text-[11px] leading-5 text-zinc-500">
                        {{ item.desc }}
                      </p>
                    </article>
                  </div>
                </div>

                <div class="grid content-start gap-3 sm:grid-cols-3 lg:grid-cols-1">
                  <article
                    v-for="metric in metrics"
                    :key="metric.label"
                    class="radar-card rounded-[1.5rem] p-5 sm:p-6"
                  >
                    <p class="text-3xl font-semibold tracking-[-0.04em]">
                      {{ metric.value }}
                    </p>
                    <p class="mt-2 text-xs leading-5 text-zinc-500">
                      {{ metric.label }}
                    </p>
                  </article>
                </div>
              </div>
            </div>
          </div>

          <!-- SEARCH DOCK -->
          <div
            class="search-dock relative z-20 mx-auto mt-6 w-[96%] rounded-[1.7rem] border border-[var(--border)] bg-[var(--glass-strong)] p-3 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:w-[92%]"
          >
            <div class="grid gap-3 lg:grid-cols-[auto_1fr_auto]">
              <label class="relative">
                <span class="sr-only">Filtrar fonte</span>

                <select
                  v-model="source"
                  class="h-12 w-full appearance-none rounded-full border border-[var(--border)] bg-[var(--surface)] py-0 pl-5 pr-10 text-sm font-semibold text-[var(--text-strong)] outline-none transition focus:border-[var(--text-strong)] lg:w-auto"
                  aria-label="Filtrar fonte"
                >
                  <option value="all">Todas as fontes</option>
                  <option value="local">Blog local</option>
                  <option value="devto">DEV.to</option>
                  <option value="hn">Hacker News</option>
                </select>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  class="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-2)]"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="m8 10 4 4 4-4" />
                </svg>
              </label>

              <label class="relative block">
                <span class="sr-only">Buscar no feed</span>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-2)]"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m20 20-3.5-3.5"></path>
                </svg>

                <input
                  v-model="q"
                  type="search"
                  :placeholder="placeholder"
                  class="h-12 w-full rounded-full border border-[var(--border)] bg-[var(--surface)] pl-11 pr-5 text-sm font-medium text-[var(--text-strong)] outline-none placeholder:text-[var(--muted-2)] focus:border-[var(--text-strong)]"
                  inputmode="search"
                  aria-label="Buscar no feed"
                  @keydown.enter.prevent="refresh"
                />
              </label>

              <div class="flex gap-2">
                <button
                  type="button"
                  @click="refresh"
                  class="h-12 flex-1 rounded-full bg-[var(--text-strong)] px-5 text-xs font-semibold text-[var(--inverse)] transition hover:-translate-y-0.5 lg:flex-none"
                >
                  Buscar
                </button>

                <button
                  type="button"
                  @click="resetQuery"
                  class="h-12 flex-1 rounded-full border border-[var(--border)] bg-[var(--surface)] px-5 text-xs font-semibold text-[var(--muted)] transition hover:-translate-y-0.5 hover:text-[var(--text-strong)] lg:flex-none"
                >
                  Limpar
                </button>
              </div>
            </div>

            <div class="mt-3 flex flex-wrap gap-2">
              <button
                v-for="topic in topics"
                :key="topic.value"
                type="button"
                @click="applyTopic(topic.value)"
                :class="[
                  'rounded-full px-4 py-2.5 text-[11px] font-semibold transition duration-300',
                  activeTopic === topic.value
                    ? 'bg-[var(--text-strong)] text-[var(--inverse)]'
                    : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:-translate-y-0.5 hover:text-[var(--text-strong)]'
                ]"
              >
                {{ topic.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONECTOR -->
    <div
      class="relative z-10 mx-auto mt-8 h-20 max-w-7xl px-5 sm:px-6 lg:px-8"
      aria-hidden="true"
    >
      <span class="flow-line"></span>
      <span class="flow-node"></span>
    </div>

    <!-- FEED -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="eyebrow">
              Feed
            </p>

            <h2 class="section-title mt-4">
              Artigos e notícias.
              <span class="text-[var(--muted-2)]">Sem ruído na leitura.</span>
            </h2>
          </div>

          <div class="lg:col-span-5">
            <p class="text-lg leading-8 text-[var(--muted)]">
              Se uma fonte externa falhar, o blog mantém conteúdos locais e
              posts de fallback para a experiência continuar funcionando.
            </p>

            <p
              v-if="!loading"
              class="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted-2)]"
              aria-live="polite"
            >
              {{ filtered.length }}
              {{ filtered.length === 1 ? 'conteúdo encontrado' : 'conteúdos encontrados' }}
            </p>
          </div>
        </div>

        <!-- LOADING -->
        <div
          v-if="loading"
          class="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-12"
          aria-label="Carregando feed"
        >
          <div
            v-for="item in 7"
            :key="item"
            :class="[
              'skeleton-card animate-pulse rounded-[2rem] border border-[var(--border)] bg-[var(--card)]',
              postSpan(item - 1)
            ]"
          >
            <div class="aspect-[16/10] rounded-t-[2rem] bg-[var(--chip)]"></div>
            <div class="space-y-3 p-7">
              <div class="h-3 w-1/4 rounded-full bg-[var(--chip)]"></div>
              <div class="h-7 w-4/5 rounded-full bg-[var(--chip)]"></div>
              <div class="h-3 w-full rounded-full bg-[var(--chip)]"></div>
              <div class="h-3 w-3/4 rounded-full bg-[var(--chip)]"></div>
            </div>
          </div>
        </div>

        <!-- ERRO TOTAL -->
        <div
          v-else-if="error && !paginated.length"
          class="mt-14 rounded-[2rem] border border-[var(--border)] bg-[var(--glass)] p-10 text-center shadow-[var(--shadow-soft)] backdrop-blur-2xl"
          role="alert"
        >
          <p class="text-lg font-semibold text-[var(--text-strong)]">
            {{ error }}
          </p>

          <p class="mt-2 text-sm text-[var(--muted)]">
            Tente novamente em alguns instantes.
          </p>

          <button
            type="button"
            @click="refresh"
            class="primary-button mt-6 inline-flex h-11 items-center justify-center rounded-full px-6 text-xs font-semibold"
          >
            Tentar novamente
          </button>
        </div>

        <div v-else>
          <div
            v-if="error"
            class="mt-10 flex items-start gap-3 rounded-[1.5rem] border border-[var(--border)] bg-[var(--glass)] p-5 text-sm leading-6 text-[var(--muted)] backdrop-blur-xl"
          >
            <span class="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--text-strong)] text-[10px] font-bold text-[var(--inverse)]">
              !
            </span>
            <p>
              {{ error }} Mesmo assim, exibimos conteúdos locais e salvos.
            </p>
          </div>

          <!-- FEATURED -->
          <div
            v-if="featuredPost"
            class="mt-14"
          >
            <router-link
              :to="{ path: `/blog/${featuredPost.slug}`, state: { post: featuredPost } }"
              class="featured-post group grid overflow-hidden rounded-[2.5rem] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-soft)] backdrop-blur-xl transition duration-500 hover:-translate-y-1 hover:shadow-[var(--shadow-large)] lg:grid-cols-[1.05fr_.95fr]"
            >
              <div class="relative min-h-[25rem] overflow-hidden bg-[var(--chip)]">
                <img
                  v-if="featuredPost.image"
                  :src="featuredPost.image"
                  :alt="featuredPost.title"
                  class="absolute inset-0 h-full w-full object-cover transition duration-1000 group-hover:scale-[1.035]"
                  loading="lazy"
                />

                <div
                  v-else
                  class="absolute inset-0 grid place-items-center bg-[#090a0c] p-8 text-center text-white"
                >
                  <div class="max-w-xl">
                    <p class="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-500">
                      {{ sourceLabel(featuredPost.source) }}
                    </p>

                    <p class="mt-5 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl">
                      {{ featuredPost.title }}
                    </p>
                  </div>
                </div>

                <div
                  v-if="featuredPost.image"
                  class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent"
                ></div>

                <span
                  class="absolute left-5 top-5 rounded-full bg-black/35 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white ring-1 ring-white/15 backdrop-blur-xl"
                >
                  Destaque
                </span>
              </div>

              <div class="flex flex-col justify-center p-7 sm:p-9 lg:p-11">
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[10px] font-semibold text-[var(--muted)]"
                  >
                    {{ sourceLabel(featuredPost.source) }}
                  </span>

                  <span
                    v-if="featuredPost.date"
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[10px] font-semibold text-[var(--muted)]"
                  >
                    {{ featuredPost.date }}
                  </span>
                </div>

                <h3
                  class="mt-6 text-4xl font-semibold tracking-[-0.045em] text-[var(--text-strong)] sm:text-5xl"
                >
                  {{ featuredPost.title }}
                </h3>

                <p class="mt-5 text-base leading-7 text-[var(--muted)]">
                  {{ featuredPost.excerpt || 'Sem resumo disponível.' }}
                </p>

                <div class="mt-7 flex flex-wrap gap-2">
                  <span
                    v-for="tag in featuredPost.tags || []"
                    :key="tag"
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--muted)]"
                  >
                    {{ tag }}
                  </span>
                </div>

                <div class="mt-8 flex items-center gap-3 text-sm font-semibold text-[var(--text-strong)]">
                  Ler destaque
                  <span
                    class="grid h-9 w-9 place-items-center rounded-full bg-[var(--text-strong)] text-[var(--inverse)] transition duration-300 group-hover:translate-x-1"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </div>
              </div>
            </router-link>
          </div>

          <!-- REGULAR POSTS -->
          <div
            v-if="regularPosts.length"
            class="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-12 xl:items-start"
          >
            <article
              v-for="(post, index) in regularPosts"
              :key="post.slug"
              :class="[
                'post-card group overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--card)] backdrop-blur-xl transition duration-500 hover:-translate-y-1.5 hover:shadow-[var(--shadow-large)]',
                postSpan(index)
              ]"
            >
              <router-link
                :to="{ path: `/blog/${post.slug}`, state: { post } }"
                class="block h-full"
              >
                <div class="relative aspect-[16/10] overflow-hidden bg-[var(--chip)]">
                  <img
                    v-if="post.image"
                    :src="post.image"
                    :alt="post.title"
                    class="h-full w-full object-cover transition duration-1000 group-hover:scale-[1.04]"
                    loading="lazy"
                  />

                  <div
                    v-else
                    class="grid h-full w-full place-items-center bg-[#090a0c] p-7 text-center text-white"
                  >
                    <div>
                      <p class="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                        {{ sourceLabel(post.source) }}
                      </p>

                      <p class="mt-4 text-2xl font-semibold tracking-[-0.035em]">
                        {{ post.title }}
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="post.image"
                    class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"
                  ></div>

                  <span
                    class="absolute bottom-4 right-4 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-sm font-semibold text-black opacity-0 shadow-lg transition duration-500 group-hover:opacity-100"
                    aria-hidden="true"
                  >
                    ↗
                  </span>
                </div>

                <div class="p-7">
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1 text-[10px] font-semibold text-[var(--muted)]"
                    >
                      {{ sourceLabel(post.source) }}
                    </span>

                    <span
                      v-if="post.date"
                      class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1 text-[10px] font-semibold text-[var(--muted)]"
                    >
                      {{ post.date }}
                    </span>
                  </div>

                  <h3
                    class="mt-5 line-clamp-2 text-2xl font-semibold tracking-[-0.03em] text-[var(--text-strong)]"
                  >
                    {{ post.title }}
                  </h3>

                  <p class="mt-3 line-clamp-3 text-sm leading-6 text-[var(--muted)]">
                    {{ post.excerpt || 'Sem resumo disponível.' }}
                  </p>

                  <div class="mt-6 flex flex-wrap gap-2">
                    <span
                      v-for="tag in post.tags || []"
                      :key="tag"
                      class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[10px] font-medium text-[var(--muted)]"
                    >
                      {{ tag }}
                    </span>
                  </div>

                  <div class="mt-8 flex items-center justify-between border-t border-[var(--border)] pt-5">
                    <span class="text-xs font-semibold text-[var(--text-strong)]">
                      Ler artigo
                    </span>

                    <span class="text-sm text-[var(--muted-2)] transition duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </div>
                </div>
              </router-link>
            </article>
          </div>

          <!-- EMPTY -->
          <div
            v-if="!paginated.length"
            class="mt-14 rounded-[2rem] border border-[var(--border)] bg-[var(--glass)] p-10 text-center shadow-[var(--shadow-soft)] backdrop-blur-2xl"
          >
            <p class="text-lg font-semibold text-[var(--text-strong)]">
              Nenhum resultado encontrado.
            </p>

            <p class="mt-2 text-sm text-[var(--muted)]">
              Tente buscar por outro termo, como Vue, Docker, IA, backend ou design.
            </p>

            <button
              type="button"
              @click="resetQuery"
              class="secondary-button mt-6 inline-flex h-10 items-center justify-center rounded-full px-5 text-xs font-semibold"
            >
              Limpar busca
            </button>
          </div>

          <!-- PAGINAÇÃO -->
          <div
            v-if="filtered.length"
            class="pagination-panel mt-10 flex flex-col gap-4 rounded-[1.7rem] border border-[var(--border)] bg-[var(--glass)] p-4 shadow-[var(--shadow-soft)] backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="text-sm font-medium text-[var(--muted)]">
              {{ filtered.length }} resultados encontrados
            </div>

            <div class="flex items-center gap-2">
              <button
                type="button"
                :disabled="page === 1"
                @click="previousPage"
                class="h-11 rounded-full border border-[var(--border)] bg-[var(--surface)] px-5 text-xs font-semibold text-[var(--text-strong)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-35"
              >
                Anterior
              </button>

              <span
                class="min-w-16 px-2 text-center text-xs font-semibold text-[var(--muted)]"
              >
                {{ page }} / {{ totalPages }}
              </span>

              <button
                type="button"
                :disabled="page >= totalPages"
                @click="nextPage"
                class="h-11 rounded-full border border-[var(--border)] bg-[var(--surface)] px-5 text-xs font-semibold text-[var(--text-strong)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-35"
              >
                Próxima
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- TRANSIÇÃO -->
    <div
      class="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8"
      aria-hidden="true"
    >
      <div class="curve-transition">
        <span class="curve-dot"></span>
      </div>
    </div>

    <!-- DARK EDITORIAL -->
    <section class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-[88rem] px-3 sm:px-5 lg:px-7">
        <div
          class="dark-panel relative overflow-hidden rounded-[2.7rem] px-5 py-16 text-white shadow-[var(--shadow-large)] sm:px-8 sm:py-20 lg:px-12 xl:px-16"
          data-reveal
        >
          <div class="dark-panel-grid pointer-events-none absolute inset-0"></div>
          <div class="dark-orb absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl"></div>

          <div class="relative grid gap-12 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-7">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Construção digital
              </p>

              <h2
                class="mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl lg:text-6xl"
              >
                Ler é parte do processo.
                <span class="block text-zinc-500">Construir é o próximo passo.</span>
              </h2>

              <p class="mt-6 max-w-2xl text-lg leading-8 text-zinc-400">
                Produto, sistema, automação ou integração. O conteúdo ajuda a
                organizar contexto; o projeto transforma isso em algo operável.
              </p>
            </div>

            <div class="grid gap-3 lg:col-span-5">
              <a
                :href="whatsHref"
                target="_blank"
                rel="noopener"
                class="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-7 text-sm font-semibold text-black transition hover:-translate-y-0.5 hover:bg-zinc-200"
              >
                Falar no WhatsApp
                <span aria-hidden="true">→</span>
              </a>

              <router-link
                to="/solutions"
                class="inline-flex h-12 items-center justify-center rounded-full bg-white/[0.07] px-7 text-sm font-semibold text-white ring-1 ring-white/10 transition hover:bg-white/[0.11]"
              >
                Ver soluções
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { posts as localPosts } from '@/data/blog'

const whatsHref = 'https://wa.me/77999631644'

const loading = ref(false)
const error = ref('')
const q = ref('')
const source = ref('all')
const activeTopic = ref('all')
const items = ref([])
const page = ref(1)
const perPage = 10
const placeholder = 'Buscar por tema, stack, produto ou notícia'

const showScrollTop = ref(false)
const pointer = reactive({ x: 50, y: 20 })

let observer = null

let debounceId = null
let requestId = 0

const metrics = [
  {
    value: 'Vários',
    label: 'tipos de conteúdo'
  },
  {
    value: 'Live',
    label: 'feed externo'
  },
  {
    value: 'Local',
    label: 'fallback ativo'
  }
]

const highlights = [
  {
    title: 'Artigos técnicos',
    desc: 'Conteúdos sobre frontend, backend, arquitetura, infra e produtividade.'
  },
  {
    title: 'Notícias',
    desc: 'Atualizações e discussões de tecnologia vindas de fontes externas.'
  },
  {
    title: 'Produto e operação',
    desc: 'Ideias sobre sistemas internos, automação, design e tomada de decisão.'
  }
]

const topics = [
  {
    value: 'all',
    label: 'Todos'
  },
  {
    value: 'frontend',
    label: 'Frontend'
  },
  {
    value: 'backend',
    label: 'Backend'
  },
  {
    value: 'ai',
    label: 'IA'
  },
  {
    value: 'devops',
    label: 'DevOps'
  },
  {
    value: 'product',
    label: 'Produto'
  },
  {
    value: 'design',
    label: 'Design'
  },
  {
    value: 'security',
    label: 'Segurança'
  }
]

const topicQueries = {
  all: '',
  frontend: 'vue nuxt react frontend javascript css tailwind',
  backend: 'nestjs node backend api postgres database spring java',
  ai: 'ai ia llm artificial intelligence agents automation',
  devops: 'docker kubernetes ci cd cloud deploy infrastructure',
  product: 'product management saas startup mvp business',
  design: 'ui ux design system interface product design',
  security: 'security cybersecurity authentication privacy api security'
}

const fallbackPosts = [
  {
    slug: 'local-produto-digital-sob-medida',
    title: 'Por que produto digital sob medida ainda faz sentido',
    excerpt: 'Quando o processo é específico, um sistema genérico costuma gerar mais adaptação do que eficiência.',
    date: '2026-01-01',
    tags: ['Produto', 'SaaS', 'Operação'],
    image: null,
    link: null,
    source: 'local',
    content: '## Produto sob medida não é sobre criar complexidade\n\nÉ sobre remover atrito. Um sistema próprio faz sentido quando a operação tem regras específicas, integrações importantes ou fluxos que uma ferramenta genérica não acompanha bem.\n\n### O ponto não é construir tudo\n\nO melhor caminho costuma ser começar pequeno, validar o fluxo principal e evoluir com base em uso real.\n\n- Menos trabalho manual\n- Mais aderência ao processo\n- Dados mais organizados\n- Evolução sem depender de limitações externas'
  },
  {
    slug: 'local-backoffice-claro',
    title: 'Como um backoffice claro reduz ruído na operação',
    excerpt: 'Painéis internos funcionam melhor quando priorizam ação, contexto e visibilidade.',
    date: '2026-01-02',
    tags: ['Backoffice', 'Operação', 'UX'],
    image: null,
    link: null,
    source: 'local',
    content: '## Um bom backoffice precisa ser óbvio\n\nQuem usa um sistema interno geralmente quer resolver uma tarefa, não interpretar uma interface complexa.\n\n### O que priorizar\n\n- Busca rápida\n- Fluxos previsíveis\n- Estados claros\n- Permissões bem definidas\n- Relatórios úteis\n\nA interface precisa orientar a operação e reduzir dúvidas.'
  },
  {
    slug: 'local-integracoes-webhooks',
    title: 'Integrações: o que pensar antes de conectar APIs',
    excerpt: 'APIs e webhooks precisam de retries, logs, idempotência e monitoramento para não virarem ponto cego.',
    date: '2026-01-03',
    tags: ['APIs', 'Integrações', 'Backend'],
    image: null,
    link: null,
    source: 'local',
    content: '## Integração não é só chamar uma API\n\nToda integração importante precisa lidar com falha, latência, dados duplicados e mudanças externas.\n\n```typescript\nasync function handleWebhook(payload) {\n  const eventId = payload.id\n\n  if (await alreadyProcessed(eventId)) {\n    return { ok: true }\n  }\n\n  await processEvent(payload)\n  await markAsProcessed(eventId)\n\n  return { ok: true }\n}\n```\n\n### Pontos essenciais\n\n- Logs claros\n- Retry automático\n- Idempotência\n- Monitoramento\n- Alertas quando algo falhar'
  },
  {
    slug: 'local-frontend-vue-nuxt-tailwind',
    title: 'Frontend moderno com Vue, Nuxt e Tailwind',
    excerpt: 'Uma base visual limpa depende de componentes consistentes, responsividade e uma boa hierarquia de interface.',
    date: '2026-01-04',
    tags: ['Frontend', 'Vue', 'Nuxt', 'Tailwind'],
    image: null,
    link: null,
    source: 'local',
    content: '## Frontend não é só aparência\n\nUm bom frontend precisa carregar rápido, orientar a navegação e manter consistência visual.\n\n### Uma stack simples\n\n- Vue para interface\n- Nuxt para estrutura\n- Tailwind para design system utilitário\n- Componentes reutilizáveis\n\nA experiência melhora quando o visual parece simples e o código continua organizado.'
  },
  {
    slug: 'local-backend-nestjs-postgres',
    title: 'Backend sólido com NestJS, Postgres e boas regras de negócio',
    excerpt: 'APIs bem desenhadas reduzem retrabalho e deixam o produto preparado para crescer.',
    date: '2026-01-05',
    tags: ['Backend', 'NestJS', 'Postgres', 'API'],
    image: null,
    link: null,
    source: 'local',
    content: '## Backend é a espinha dorsal do produto\n\nA interface pode ser simples, mas as regras precisam estar bem protegidas no backend.\n\n### Pontos importantes\n\n- Autenticação\n- Permissões\n- Validação de dados\n- Logs\n- Banco bem modelado\n- APIs previsíveis\n\nCom isso, o produto cresce com menos risco.'
  },
  {
    slug: 'local-ia-agentes-automacao',
    title: 'IA e agentes aplicados à operação',
    excerpt: 'Agentes de IA funcionam melhor quando resolvem tarefas específicas e se conectam a dados reais.',
    date: '2026-01-06',
    tags: ['IA', 'LLM', 'Agentes', 'Automação'],
    image: null,
    link: null,
    source: 'local',
    content: '## IA precisa de contexto\n\nUm agente só entrega valor quando entende a tarefa, acessa os dados certos e executa um fluxo claro.\n\n### Bons usos\n\n- Atendimento\n- Resumo de dados\n- Classificação de solicitações\n- Geração de relatórios\n- Apoio operacional\n\nO foco deve ser produtividade, não efeito visual.'
  },
  {
    slug: 'local-devops-docker-deploy',
    title: 'Deploy previsível com Docker e CI/CD',
    excerpt: 'Uma esteira simples de deploy evita falhas manuais e deixa a entrega mais segura.',
    date: '2026-01-07',
    tags: ['DevOps', 'Docker', 'CI/CD', 'Deploy'],
    image: null,
    link: null,
    source: 'local',
    content: '## Deploy precisa ser repetível\n\nQuando o deploy depende de passos manuais, o risco aumenta.\n\n### Uma boa base\n\n- Docker\n- Variáveis de ambiente\n- Pipeline simples\n- Logs\n- Monitoramento\n- Rollback possível\n\nO objetivo é publicar com confiança.'
  },
  {
    slug: 'local-seguranca-autenticacao',
    title: 'Segurança básica que todo produto deveria ter',
    excerpt: 'Autenticação, permissões, validação e logs são decisões essenciais desde o começo.',
    date: '2026-01-08',
    tags: ['Segurança', 'Autenticação', 'API', 'Privacidade'],
    image: null,
    link: null,
    source: 'local',
    content: '## Segurança começa no desenho\n\nNão é preciso esperar o produto crescer para pensar em segurança.\n\n### O mínimo importante\n\n- Autenticação bem feita\n- Controle de permissões\n- Validação de entrada\n- Proteção de dados sensíveis\n- Logs de eventos importantes\n- Boas práticas em APIs\n\nA base segura evita retrabalho depois.'
  }
]

watch([q, source], () => {
  page.value = 1
  clearTimeout(debounceId)
  debounceId = setTimeout(() => {
    refresh()
  }, 500)
})

function sourceLabel(value) {
  if (value === 'devto') return 'DEV.to'
  if (value === 'hn') return 'Hacker News'
  if (value === 'local') return 'Blog'
  return value || 'Fonte'
}

function resetQuery() {
  q.value = ''
  source.value = 'all'
  activeTopic.value = 'all'
  page.value = 1
}

function applyTopic(topic) {
  activeTopic.value = topic
  q.value = topicQueries[topic] || ''
  page.value = 1
  clearTimeout(debounceId)
  refresh()
}

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function getSearchTerms(value) {
  return normalize(value)
    .split(/\s+/)
    .map(term => term.trim())
    .filter(term => term.length >= 2)
}

function getPostContent(item) {
  return normalize([
    item.title,
    item.excerpt,
    item.source,
    item.date,
    ...(item.tags || [])
  ].join(' '))
}

function matchesTerms(item, query) {
  const terms = getSearchTerms(query)

  if (!terms.length) {
    return true
  }

  const content = getPostContent(item)

  return terms.some(term => content.includes(term))
}

function normalizePost(item, origin = 'local') {
  return {
    slug: item.slug || '',
    title: item.title || 'Sem título',
    excerpt: item.excerpt || item.description || '',
    date: item.date || '',
    tags: item.tags || [],
    image: item.image || null,
    link: item.link || null,
    source: item.source || origin,
    content: item.content || '',
    contentHtml: item.contentHtml || ''
  }
}

function getLocalPosts() {
  const mapped = Array.isArray(localPosts)
    ? localPosts.map(item => normalizePost(item, 'local'))
    : []

  const fallback = fallbackPosts.map(item => normalizePost(item, 'local'))
  const map = new Map()

  for (const item of [...mapped, ...fallback]) {
    if (item.slug && !map.has(item.slug)) {
      map.set(item.slug, item)
    }
  }

  return [...map.values()]
}

async function fetchDevto(query) {
  const terms = getSearchTerms(query).slice(0, 6)
  const tags = terms.length ? terms : ['javascript', 'vue', 'webdev', 'programming']

  const urls = tags.map(tag => {
    return `https://dev.to/api/articles?per_page=20&tag=${encodeURIComponent(tag)}`
  })

  const responses = await Promise.allSettled(urls.map(url => fetch(url)))

  const arrays = await Promise.all(
    responses.map(result => {
      if (result.status === 'fulfilled' && result.value.ok) {
        return result.value.json()
      }

      return []
    })
  )

  const map = new Map()

  for (const article of arrays.flat()) {
    const id = `devto-${article.id}`

    if (!map.has(id)) {
      map.set(id, {
        slug: id,
        title: article.title,
        excerpt: article.description || article.title || '',
        date: (article.published_at || '').slice(0, 10),
        tags: (article.tag_list || []).slice(0, 4),
        link: article.url,
        image: article.cover_image || article.social_image || null,
        source: 'devto',
        content: ''
      })
    }
  }

  return [...map.values()]
}

async function fetchHN(query) {
  const terms = getSearchTerms(query)
  const search = terms.length ? terms.join(' OR ') : 'technology OR startup OR programming OR software'
  const url = `https://hn.algolia.com/api/v1/search_by_date?tags=story&hitsPerPage=50&query=${encodeURIComponent(search)}`
  const response = await fetch(url)

  if (!response.ok) {
    return []
  }

  const data = await response.json()

  return (data.hits || []).map(item => {
    const link = item.url || `https://news.ycombinator.com/item?id=${item.objectID}`

    return {
      slug: `hn-${item.objectID}`,
      title: item.title || item.story_title || '(sem título)',
      excerpt: item._highlightResult?.title?.value?.replace(/<[^>]+>/g, '') || link || '',
      date: (item.created_at || '').slice(0, 10),
      tags: ['Hacker News'],
      link,
      image: null,
      source: 'hn',
      content: ''
    }
  })
}

async function refresh() {
  const currentRequest = ++requestId
  loading.value = true
  error.value = ''

  const local = getLocalPosts()
  let results = []

  try {
    if (source.value === 'all' || source.value === 'local') {
      results = results.concat(local)
    }

    if (source.value === 'all' || source.value === 'devto') {
      const devto = await fetchDevto(q.value)
      results = results.concat(devto)
    }

    if (source.value === 'all' || source.value === 'hn') {
      const hn = await fetchHN(q.value)
      results = results.concat(hn)
    }

    if (!results.length) {
      results = local
    }

    results = dedupePosts(results)
    results.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))

    if (currentRequest === requestId) {
      items.value = results
    }
  } catch {
    if (currentRequest === requestId) {
      error.value = 'Não foi possível carregar todas as fontes externas.'
      items.value = dedupePosts(local)
    }
  } finally {
    if (currentRequest === requestId) {
      loading.value = false
    }
  }
}

function dedupePosts(posts) {
  const map = new Map()

  for (const item of posts) {
    const normalized = normalizePost(item, item.source || 'local')

    if (normalized.slug && !map.has(normalized.slug)) {
      map.set(normalized.slug, normalized)
    }
  }

  return [...map.values()]
}

function previousPage() {
  if (page.value > 1) {
    page.value--
  }
}

function nextPage() {
  if (page.value < totalPages.value) {
    page.value++
  }
}

const filtered = computed(() => {
  return items.value.filter(item => matchesTerms(item, q.value))
})

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(filtered.value.length / perPage))
})

const startIndex = computed(() => {
  return (page.value - 1) * perPage
})

const endIndex = computed(() => {
  return startIndex.value + perPage
})

const paginated = computed(() => {
  return filtered.value.slice(startIndex.value, endIndex.value)
})

const featuredPost = computed(() => {
  return paginated.value[0] || null
})

const regularPosts = computed(() => {
  return paginated.value.slice(1)
})



function postSpan(index) {
  const layout = [
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-10',
    'xl:col-span-5',
    'xl:col-span-7 xl:mt-8',
    'xl:col-span-6',
    'xl:col-span-6 xl:mt-8',
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-10',
    'xl:col-span-12'
  ]

  return layout[index % layout.length]
}

function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1

  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function handleScroll() {
  showScrollTop.value = window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

function setupReveal() {
  const elements = document.querySelectorAll('[data-reveal]')

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(element => element.classList.add('is-visible'))
    return
  }

  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer?.unobserve(entry.target)
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -45px 0px'
    }
  )

  elements.forEach(element => observer.observe(element))
}


onMounted(() => {
  refresh()
  setupReveal()
  handleScroll()

  window.addEventListener('scroll', handleScroll, {
    passive: true
  })
})

onUnmounted(() => {
  clearTimeout(debounceId)
  observer?.disconnect()
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, 0.90);
  --card: rgba(255, 255, 255, 0.76);
  --chip: rgba(245, 245, 247, 0.92);
  --glass: rgba(255, 255, 255, 0.54);
  --glass-strong: rgba(255, 255, 255, 0.84);
  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #ffffff;
  --border: rgba(24, 24, 27, 0.085);
  --focus: rgba(113, 113, 122, 0.18);
  --shadow-soft: 0 12px 38px rgba(24, 24, 27, 0.07);
  --shadow-large: 0 32px 100px rgba(24, 24, 27, 0.105);
  --hero-a: #0a0a0b;
  --hero-b: #7c7f87;
  --orb-one: rgba(147, 161, 185, 0.22);
  --orb-two: rgba(203, 213, 225, 0.31);
  --orb-three: rgba(226, 232, 240, 0.28);
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, 0.90);
  --card: rgba(19, 20, 24, 0.76);
  --chip: rgba(26, 27, 31, 0.92);
  --glass: rgba(19, 20, 23, 0.54);
  --glass-strong: rgba(18, 19, 22, 0.84);
  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;
  --border: rgba(255, 255, 255, 0.085);
  --focus: rgba(212, 212, 216, 0.13);
  --shadow-soft: 0 12px 42px rgba(0, 0, 0, 0.30);
  --shadow-large: 0 36px 120px rgba(0, 0, 0, 0.50);
  --hero-a: #ffffff;
  --hero-b: #777a84;
  --orb-one: rgba(71, 85, 105, 0.23);
  --orb-two: rgba(63, 63, 70, 0.28);
  --orb-three: rgba(39, 39, 42, 0.28);
}

:global(html) {
  background: var(--page);
  transition: background-color 350ms ease;
}

.site-page {
  transition:
    background-color 350ms ease,
    color 350ms ease;
}

.ambient-grid {
  opacity: 0.31;
  background-image:
    linear-gradient(to right, var(--border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 82%);
}

.ambient-noise {
  opacity: 0.021;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E");
}

.pointer-glow {
  background:
    radial-gradient(
      42rem circle at var(--pointer-x) var(--pointer-y),
      var(--orb-one),
      transparent 68%
    );
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(95px);
  will-change: transform;
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: var(--orb-one);
  animation: float-a 14s ease-in-out infinite;
}

.ambient-orb-two {
  right: -12rem;
  top: 50rem;
  width: 38rem;
  height: 38rem;
  background: var(--orb-two);
  animation: float-b 18s ease-in-out infinite;
}

.ambient-orb-three {
  left: 25%;
  top: 135rem;
  width: 30rem;
  height: 30rem;
  background: var(--orb-three);
  animation: float-c 21s ease-in-out infinite;
}

.hero-gradient {
  background: linear-gradient(120deg, var(--hero-a) 5%, var(--hero-b) 96%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-halo {
  background: var(--orb-one);
}

.primary-button {
  color: var(--inverse);
  background: var(--text-strong);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition:
    transform 300ms ease,
    box-shadow 300ms ease;
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0 15px 32px rgba(0, 0, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.13);
}

.secondary-button {
  color: var(--text-strong);
  background: var(--glass-strong);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(18px);
  transition:
    transform 300ms ease,
    border-color 300ms ease;
}

.secondary-button:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--text-strong) 18%, transparent);
}

.radar-frame {
  background:
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--text-strong) 16%, transparent),
      color-mix(in srgb, var(--text-strong) 4%, transparent)
    );
  border: 1px solid var(--border);
  box-shadow:
    var(--shadow-large),
    inset 0 1px 0 rgba(255, 255, 255, 0.10);
}

.radar-card {
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.075);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
  transition:
    background-color 300ms ease,
    transform 300ms ease;
}

.radar-card:hover {
  background: rgba(255, 255, 255, 0.065);
  transform: translateY(-2px);
}

.search-dock {
  transition:
    transform 400ms ease,
    box-shadow 400ms ease;
}

.search-dock:focus-within {
  transform: translateY(-2px);
  box-shadow: var(--shadow-large);
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background:
    linear-gradient(
      to bottom,
      transparent,
      var(--border) 25%,
      var(--border) 75%,
      transparent
    );
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--page);
  border-radius: 9999px;
  background: var(--text-strong);
  box-shadow:
    0 0 0 5px color-mix(in srgb, var(--text-strong) 8%, transparent);
  transform: translate(-50%, -50%);
}

.eyebrow {
  font-size: 0.72rem;
  line-height: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--muted);
}

.section-title {
  max-width: 52rem;
  font-size: clamp(2.35rem, 4.7vw, 4rem);
  line-height: 1.02;
  font-weight: 600;
  letter-spacing: -0.055em;
  color: var(--text-strong);
}

.post-card,
.featured-post {
  backface-visibility: hidden;
}

.curve-transition {
  position: relative;
  width: 100%;
  height: 5.5rem;
  overflow: hidden;
}

.curve-transition::before {
  content: '';
  position: absolute;
  left: 12%;
  top: 50%;
  width: 76%;
  height: 5rem;
  border-top: 1px solid var(--border);
  border-radius: 50%;
  transform: translateY(18%);
}

.curve-dot {
  position: absolute;
  left: 50%;
  top: 2.25rem;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--text-strong);
  transform: translateX(-50%);
}

.dark-panel {
  background:
    radial-gradient(circle at 85% 20%, rgba(255, 255, 255, 0.07), transparent 30%),
    #090a0c;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 36px 110px rgba(0, 0, 0, 0.22);
}

.dark-panel-grid {
  opacity: 0.31;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to left, black, transparent 74%);
}

.dark-orb {
  background: rgba(255, 255, 255, 0.07);
}

[data-reveal] {
  opacity: 0;
  transform: translateY(26px) scale(.992);
  transition:
    opacity 780ms cubic-bezier(.22, .82, .22, 1),
    transform 780ms cubic-bezier(.22, .82, .22, 1);
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
}

@keyframes float-a {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(3rem, 2rem, 0); }
}

@keyframes float-b {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(-3rem, -2rem, 0); }
}

@keyframes float-c {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(1rem, -3rem, 0); }
}

@media (max-width: 640px) {
  .ambient-grid {
    background-size: 44px 44px;
  }

  .curve-transition::before {
    left: 2%;
    width: 96%;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  [data-reveal] {
    opacity: 1;
    transform: none;
  }
}
</style>